import { Cell, Grid } from 'bold-ui'
import { useAcessoLotacaoOrEstagio } from 'components/auth/useAcessoLotacao'
import { Form, FormRenderProps } from 'components/form'
import { useListFieldStatus } from 'components/form/core/hooks'
import { getFieldError } from 'components/form/core/util'
import { ProcedimentoSelectField, ProcedimentoSelectModel } from 'components/form/field/select/ProcedimentoSelectField'
import { TipoProcedimentoEnum } from 'graphql/types.generated'
import { useCallback } from 'react'
import { MetaArray, metaPath } from 'util/metaPath'
import { isEmpty } from 'util/validation/Util'
import { SoapEditableItem } from 'view/atendimentos/atendimento-individual/model'
import { EditableList, useEditableListField } from 'view/atendimentos/detail/components/EditableList'
import { ProcedimentoModel } from 'view/atendimentos/model-procedimento'
import { CidadaoAtendimento } from 'view/atendimentos/types/CidadaoAtendimento'
import { getSexoCidadao } from 'view/atendimentos/utils/util-sexoCidadao'

import { getCbosIdsProcedimentos } from '../../plano/utils/util-plano'
import { GRUPOS_PROCEDIMENTOS_PADRAO } from '../constants-finalizacao'
import { ProcedimentoSigtapRow } from './ProcedimentoSigtapRow'

export interface ProcedimentoSigtapFieldModel extends ProcedimentoModel, SoapEditableItem {}

export interface ProcedimentoSigtapFieldProps {
  name: MetaArray<ProcedimentoSigtapFieldModel>
  handleRemoveProcedimentoAutomatico?: (itemToRemove: ProcedimentoSigtapFieldModel) => void
  cidadao?: CidadaoAtendimento
  tipoProcedimento?: TipoProcedimentoEnum
  label?: string
  dataAtendimento: LocalDate
  controlModifiedStatus?: boolean
}

ProcedimentoSigtapField.defaultProps = {
  tipoProcedimento: TipoProcedimentoEnum.ADMINISTRATIVO,
}

const path = metaPath<ProcedimentoSigtapFieldModel>()

export function ProcedimentoSigtapField(props: ProcedimentoSigtapFieldProps) {
  const {
    name,
    cidadao,
    tipoProcedimento,
    label,
    dataAtendimento,
    handleRemoveProcedimentoAutomatico,
    controlModifiedStatus = false,
  } = props

  const { acesso } = useAcessoLotacaoOrEstagio()

  const {
    handleSubmit,
    removeItem,
    resetItemToInitialValue,
    meta,
    input: { value },
  } = useEditableListField({
    name,
  })

  const handleRemoveItem = handleRemoveProcedimentoAutomatico ?? removeItem

  const { fieldStatus, disabledValues: procedimentosDisabled } = useListFieldStatus({
    name,
    controlModifiedStatus,
    isSameRow,
  })

  const procedimentoIsEqual = (procedimento: ProcedimentoSelectModel) => {
    return (value || []).some((o2) => procedimento.id === o2.procedimento.id)
  }

  const itemIsDisabled = useCallback(
    (item: ProcedimentoSelectModel) =>
      procedimentosDisabled?.map((itemDisabled) => itemDisabled?.procedimento.id).includes(item.id),
    [procedimentosDisabled]
  )

  const renderForm = (props: FormRenderProps<ProcedimentoSigtapFieldModel>) => {
    const handleChange = (newValue: ProcedimentoSelectModel) => {
      newValue && !procedimentoIsEqual(newValue) && props.form.submit()
    }

    return (
      <ProcedimentoSelectField
        name={path.procedimento}
        label={label}
        tipoProcedimento={tipoProcedimento}
        gruposProcedimento={GRUPOS_PROCEDIMENTOS_PADRAO}
        sexo={getSexoCidadao(cidadao)}
        dataNascimento={cidadao?.dataNascimento}
        dataAtendimento={dataAtendimento}
        cboIds={getCbosIdsProcedimentos(acesso)}
        itemIsEqual={procedimentoIsEqual}
        onChange={handleChange}
        loadItemsOnOpen={false}
        error={getFieldError(meta)}
        itemIsDisabled={!isEmpty(procedimentosDisabled) ? itemIsDisabled : undefined}
      />
    )
  }

  return (
    <Grid gapVertical={0.5}>
      <Cell size={8}>
        <Form<ProcedimentoSigtapFieldModel> render={renderForm} onSubmit={handleSubmit} {...props} />
      </Cell>

      {!controlModifiedStatus && value?.length > 0 && (
        <Cell size={12}>
          <EditableList>
            {value?.map((item) => (
              <ProcedimentoSigtapRow key={item._id} model={item} onRemove={handleRemoveItem} />
            ))}
          </EditableList>
        </Cell>
      )}

      {controlModifiedStatus && fieldStatus?.size > 0 && (
        <Cell size={12}>
          <EditableList>
            {Array.from(fieldStatus, ([item, status]) => (
              <ProcedimentoSigtapRow
                key={item._id}
                model={item}
                onRemove={handleRemoveItem}
                onReset={resetItemToInitialValue}
                status={status}
                controlModifiedStatus
              />
            ))}
          </EditableList>
        </Cell>
      )}
    </Grid>
  )
}

const isSameRow = (itemA: ProcedimentoSigtapFieldModel, itemB: ProcedimentoSigtapFieldModel) =>
  itemA.procedimento.id === itemB.procedimento.id
